import { Controller } from '@hotwired/stimulus'

import 'mapbox.js'
import 'leaflet.markercluster'

import flagIcon from '../icons/flag_icon'
import * as analytics from '../utils/analytics'
import openModal from '../utils/modal'

/* global L */

const CLUSTER_LIMIT = 6

const track = props => {
  const { title, location, url } = props
  analytics.track('Map icon clicked', {
    title,
    location,
    url
  })
  analytics.track('mapIcon_clicked', {
    object: 'mapIcon',
    action: 'clicked',
    subject_name: title,
    location
  }, { adapter: 'segment' })
}

export default class extends Controller {
  static get targets () {
    return ['map', 'overlay', 'cluster']
  }

  connect () {
    this.initMap()
    this.initClusters()
    this.initLocations()
    let mapPadding
    if (this.cluster) {
      this.map.addLayer(this.markerClusterGroup)
      mapPadding = 1.25
    } else {
      this.markersLayer.addTo(this.map)
      mapPadding = 0.12
    }
    const maxZoom = this.data.get('variant') === 'single' ? 8 : 12
    this.map.fitBounds(this.markersLayer.getBounds().pad(mapPadding), {
      maxZoom
    })
    this.mapTarget.classList.add('is-loaded')
  }

  initMap () {
    const accessToken = window.provenance.mapboxAccessToken
    const username = window.provenance.mapboxUsername
    const styleID = this.data.get('styleid')
    // cluster the markers
    this.cluster = false

    L.mapbox.accessToken = accessToken
    this.map = L.mapbox.map(this.mapTarget, null, {
      center: [51.505, -0.09],
      attributionControl: { compact: true },
      maxZoom: 16,
      minZoom: 1,
      scrollWheelZoom: false,
      zoomControl: false
    })

    // Style the underlying map
    const mapboxStyleURL = `mapbox://styles/${username}/${styleID}`
    L.mapbox
      .styleLayer(mapboxStyleURL)
      .addTo(this.map)

    this.map.addEventListener('click', _ => {
      analytics.track('Map clicked', { url: window.location.href })

      analytics.track('map_clicked', {
        object: 'map',
        action: 'clicked'
      }, { adapter: 'segment' })
    })
  }

  initLocations () {
    const geojson = JSON.parse(this.data.get('geojson'))

    if (
      geojson &&
      this.hasClusterTarget &&
      geojson.features &&
      geojson.features.length > CLUSTER_LIMIT
    ) {
      this.cluster = true
      this.markersLayer = L.geoJSON(geojson, {
        onEachFeature: this.onEachFeatureCluster.bind(this)
      })
    } else if (geojson) {
      this.markersLayer = L.geoJSON(geojson, {
        onEachFeature: this.onEachFeature
      })
    } else {
      this.markersLayer = L.featureGroup()
      const coordinatesData = this.data.get('coordinates')
      if (coordinatesData) {
        const variant = this.data.get('variant')
        if (variant === 'single') {
          this.initSingleLocation(coordinatesData)
        } else if (variant === 'multiple') {
          this.initMultipleLocations(coordinatesData)
        }
      } else {
        const markersData = JSON.parse(this.data.get('markers'))
        for (let i = markersData.length - 1; i >= 0; --i) {
          const markerData = markersData[i]
          this.markersLayer.addMarker(
            markerData.location,
            markerData.icon,
            markerData.name
          )
        }
      }
    }
  }

  initClusters () {
    this.markerClusterGroup = L.markerClusterGroup()
  }

  /* eslint-disable class-methods-use-this */
  onEachFeatureCluster (marker, layer) {
    this.onEachFeature(marker, layer)
    this.markerClusterGroup.addLayer(layer)
  }

  onEachFeature (marker, layer) {
    /* eslint-disable camelcase */
    const {
      title,
      description,
      icon,
      location,
      modal_url,
      organization_type_name,
      slug
    } = marker.properties

    if (icon) {
      layer.setIcon(L.divIcon(icon))
    }

    if (modal_url) {
      const el = document.querySelector('[data-experience-id]')
      const queryString = el && '?experience=' + el.dataset.experienceId

      layer.on('click', e => {
        openModal({
          url: `${modal_url}${queryString || ''}`,
          slug,
          behavior: 'drawer',
          variant: 'small'
        })
      })
      return
    }

    layer.options.riseOnHover = true
    layer
      .on('popupopen', e => {
        e.sourceTarget.setZIndexOffset(250)
        track(e.target.feature.properties)
      })
      // return clicked marker to previous position
      .on('popupclose', e => {
        e.sourceTarget.setZIndexOffset(0)
      })

    let content = ''

    if (title !== 'Unknown') {
      content += `<div class="Mapbox-markerTitle CaptionUI--emphasis">${title}</div>`
    }

    if (organization_type_name) {
      content += `<div class="Text-bodySansSmall u-marginTop4">${organization_type_name}</div>`
    }

    if (description) {
      content += `<div class="Text-bodySansSmall u-marginTop4">${description}</div>`
    }

    content += `
      <div class="Mapbox-markerLocation Text-bodySansSmallest">
        ${flagIcon} ${location}
      </div>
    `

    layer.bindPopup(content, {
      closeButton: false
    })
  }

  initSingleLocation (coordinatesData) {
    let coordinates = JSON.parse(coordinatesData)
    let zoom = 8
    if (coordinatesData === 'unknown') {
      coordinates = [0, 0]
      zoom = 1
    }

    this.addMarker(coordinates)
    this.map.setView(coordinates, zoom)
  }

  initMultipleLocations (coordinatesData) {
    const coordinatesList = JSON.parse(coordinatesData)
    for (let i = coordinatesList.length - 1; i >= 0; i -= 1) {
      this.addMarker(coordinatesList[i])
    }
  }

  addMarker (coordinates, iconUrl = null, label = null) {
    const [latitude, longitude] = coordinates
    const latSymbol = latitude >= 0 ? 'N' : 'S'
    const lngSymbol = longitude >= 0 ? 'E' : 'W'
    const formattedLat = Math.abs(latitude.toFixed(6))
    const formattedLng = Math.abs(longitude.toFixed(6))
    const markerTitle =
      label != null
        ? label
        : `${latSymbol}: ${formattedLat}, ${lngSymbol}: ${formattedLng}`

    const markerGroup = L.mapbox.featureLayer({
      type: 'Feature',
      geometry: {
        type: 'Point',
        coordinates: [longitude, latitude]
      },
      properties: {
        title: markerTitle,
        'marker-color': '#3b2d51'
      }
    })

    if (iconUrl) {
      const marker = markerGroup.getLayers()[0]
      marker.setIcon(
        L.icon({
          className: 'MarketingMap-icon',
          iconUrl: `${iconUrl}?w=40&h=40`,
          iconRetinaUrl: `${iconUrl}?w=80&h=80`,
          iconSize: [40, 40],
          iconAnchor: [20, 20],
          popupAnchor: [0, -20],
          title: markerTitle
        })
      )
    }
    this.markersLayer.addLayer(markerGroup)
  }

  showControls (event) {
    event.preventDefault()
    this.overlayTarget.classList.add('is-hidden')
    const zoomControl = L.control.zoom({ position: 'bottomright' })
    zoomControl.addTo(this.map)
  }
}
